import React, { useState } from "react";
import { motion } from "framer-motion";

// 🖼 Local assets
import girafe from "../assets/girafe.jpg";
import ankole from "../assets/ankole.jpg";
import client from "../assets/client.jpg";
import gorilland from "../assets/gorilland.jpg";

// Image list with titles
const images = [
  { src: girafe, title: "Giraffe in Uganda" },
  { src: ankole, title: "Ankole Longhorn Cattle" },
  { src: client, title: "Safari Adventure Experience" },
  { src: gorilland, title: "Mountain Gorillas of Bwindi" },
  { src: "https://images.unsplash.com/photo-1501785888041-af3ef285b470", title: "Serengeti National Park, Tanzania" },
  { src: "https://images.unsplash.com/photo-1508672019048-805c876b67e2", title: "Masai Mara, Kenya" },
  { src: "https://images.unsplash.com/photo-1500530855697-b586d89ba3ee", title: "Amboseli Elephants, Kenya" },
  { src: "https://images.unsplash.com/photo-1519681393784-d120267933ba", title: "Hot Air Balloon Safari" },
];

export default function Gallery() {
  const [selectedIndex, setSelectedIndex] = useState(null);

  const nextImage = () => {
    if (selectedIndex !== null) {
      setSelectedIndex((selectedIndex + 1) % images.length);
    }
  };

  const prevImage = () => {
    if (selectedIndex !== null) {
      setSelectedIndex((selectedIndex - 1 + images.length) % images.length);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-yellow-50 via-white to-green-50">
      {/* Banner Section */}
      <motion.div
        className="relative h-[60vh] bg-cover bg-center"
        style={{ backgroundImage: 'url("https://images.unsplash.com/photo-1501785888041-af3ef285b470")' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
          <motion.div
            className="text-center text-white"
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 0.3 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-4 drop-shadow-lg">
              Explore East Africa
            </h1>
            <p className="text-lg md:text-xl drop-shadow-md">
              Safaris • Wildlife • Mountains • Lakes • Culture
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Gallery Section */}
      <div className="p-6 max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold mb-6 text-center text-gray-800">
          Discover the Beauty
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {images.map((img, idx) => (
            <motion.div
              key={idx}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="overflow-hidden rounded-2xl shadow-lg cursor-pointer bg-white"
              onClick={() => setSelectedIndex(idx)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
            >
              <img
                src={img.src}
                alt={img.title}
                className="w-full h-60 object-cover transition-transform duration-300 hover:scale-110"
              />
              <div className="p-3 text-center bg-gray-50">
                <h3 className="text-sm md:text-base font-semibold text-gray-700">
                  {img.title}
                </h3>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedIndex !== null && (
        <motion.div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="relative max-w-4xl w-full p-4 flex flex-col items-center">
            {/* Close Button */}
            <motion.button
              className="absolute top-4 right-4 text-white bg-black/60 rounded-full p-2 hover:bg-black transition"
              onClick={() => setSelectedIndex(null)}
              whileHover={{ scale: 1.1 }}
            >
              ✕
            </motion.button>

            {/* Prev Button */}
            <motion.button
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-white bg-black/60 rounded-full p-3 hover:bg-black transition"
              onClick={prevImage}
              whileHover={{ scale: 1.1 }}
            >
              ‹
            </motion.button>

            {/* Image */}
            <motion.img
              src={images[selectedIndex].src}
              alt={images[selectedIndex].title}
              className="rounded-2xl shadow-lg w-full max-h-[75vh] object-contain"
              key={selectedIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            />

            {/* Title under Image */}
            <motion.p
              className="mt-4 text-center text-white text-lg font-semibold"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              {images[selectedIndex].title}
            </motion.p>

            {/* Next Button */}
            <motion.button
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-white bg-black/60 rounded-full p-3 hover:bg-black transition"
              onClick={nextImage}
              whileHover={{ scale: 1.1 }}
            >
              ›
            </motion.button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
